import { defaultLocale, findSeoPage, locales, pageUrl } from './seo';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Head tags for the current URL: title, description, canonical, hreflang alternates and
 * Open Graph for the indexable pages listed in seo/pages.json, `noindex` for everything else
 * (login, dashboard and other private pages, unknown paths).
 * The prerender script (scripts/prerender.js) snapshots these tags into the static HTML.
 */
function SeoHead() {
  const { pathname } = useLocation();
  const match = findSeoPage(pathname);
  const { t } = useTranslation('translation', { lng: match?.locale });

  if (!match) {
    return (
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
    );
  }

  const { locale, page } = match;
  const title = page.key === 'home'
    ? t(`seo.${page.key}.title`)
    : `${t(`seo.${page.key}.title`)} | EU4UA`;
  const description = t(`seo.${page.key}.description`);
  const url = pageUrl(locale, page.path);

  return (
    <Helmet htmlAttributes={{ lang: locale }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {locales.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={pageUrl(l, page.path)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={pageUrl(defaultLocale, page.path)} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={locale} />
    </Helmet>
  );
}

export default SeoHead;
